import axios from "axios";
import { API_URL } from "@/config";
import { addInstitutionToMealsToRelase } from "./dtos";

let abortController = {};

const store = {
  namespaced: true,

  actions: {
    async getMany(_, { filters, pageNumber, perPage }) {
      let q = {
        ...filters,
        page: pageNumber,
      };

      if (perPage) {
        q.perPage = perPage;
      }

      const res = await axios.get(`${API_URL}/meals`, { params: q });

      if (!res.data || typeof res.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getOne(_, { mealId, withDefaultIngredients, withPrices }) {
      let q = {};

      if (withDefaultIngredients) {
        q.withDefaultIngredients = true;
      }

      // withPrices only takes effect together with withDefaultIngredients — prices live on
      // the expanded ingredients branch of the response, which otherwise isn't returned.
      if (withPrices && withDefaultIngredients) {
        q.withPrices = true;
      }

      const res = await axios.get(`${API_URL}/meals/${mealId}`, { params: q });

      if (!res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async create(_, mealData) {
      const res = await axios.post(`${API_URL}/meals`, mealData);

      if (res.status !== 201 || !res.data || typeof res.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async update(_, { mealId, mealData }) {
      const res = await axios.put(`${API_URL}/meals/${mealId}`, mealData);

      if (res.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async remove(_, mealId) {
      const res = await axios.delete(`${API_URL}/meals/${mealId}`);

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async bulkRemove(_, mealIds) {
      const res = await axios.delete(`${API_URL}/meals/bulkDelete`, {
        data: {
          mealIds,
        },
      });

      if (res.status !== 200 || !res.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getMealsToRelease(_, criteria) {
      abortController?.["mealsToReleaseReport"]?.abort();
      abortController["mealsToReleaseReport"] = new AbortController();

      const response = await axios.get(`${API_URL}/meals/mealsToRelease`, {
        params: criteria,
        signal: abortController["mealsToReleaseReport"].signal,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return {
        ...response.data,
        results: {
          ...response.data.results,
          items: addInstitutionToMealsToRelase(response.data.results.items),
        },
      };
    },

    async getPeriodicReport(_, criteria) {
      const response = await axios.get(`${API_URL}/meals/periodicReport`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getNestedMealsToRelease(_, criteria) {
      abortController?.["mealsToReleaseReport"]?.abort();
      abortController["mealsToReleaseReport"] = new AbortController();

      const response = await axios.get(`${API_URL}/meals/nestedMealsToRelease`, {
        params: criteria,
        signal: abortController["mealsToReleaseReport"].signal,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOptions(_, criteria = {}) {
      const res = await axios.get(`${API_URL}/meals/options`, { params: criteria });

      if (!res.data || typeof res.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return res.data;
    },

    async getMealsToPrepare(_, criteria = {}) {
      const response = await axios.get(`${API_URL}/meals/mealsToPrepare`, {
        params: criteria,
      });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getMealImageUrlByMealId(_, mealId) {
      const response = await axios.get(`${API_URL}/mealsImages/meals/${mealId}`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async updateMealImageForMeal(_, { mealId, formData }) {
      const response = await axios({
        method: "POST",
        url: `${API_URL}/mealsImages/meals/${mealId}`,
        data: formData,
      });

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async removeMealImageForMeal(_, mealId) {
      const response = await axios.delete(`${API_URL}/mealsImages/meals/${mealId}`);

      if (response.status !== 200) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },
  },
};

export default store;
